import React, { useState, useEffect } from 'react';
import { X, Plus, Minus, Users, Globe, CreditCard, Building, AlertCircle, Check, Loader2, UserPlus, Mail } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useAuthContext } from '@/lib/AuthProvider';
import { useUserProfile } from '@/context/UserProfileContext';
import { useAffiliate } from '@/context/AffiliateContext';
import { SocialProfile, PaymentInfo, JamaicanBankInfo } from '@/types/affiliate';

interface AffiliateSignupModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const socialPlatforms = [
  'Instagram',
  'TikTok',
  'YouTube',
  'Facebook',
  'Twitter/X',
  'LinkedIn',
  'Pinterest',
  'Snapchat',
  'Other'
];

const jamaicanBanks = [
  'Scotia Bank Jamaica',
  'National Commercial Bank (NCB)',
  'JN Bank',
  'First Global Bank',
  'CIBC FirstCaribbean',
  'Sagicor Bank',
  'Republic Bank',
  'Development Bank of Jamaica',
  'Other'
];

const accountTypes = [
  { value: 'savings', label: 'Savings' },
  { value: 'chequing', label: 'Chequing' }
];

const AffiliateSignupModal = ({ isOpen, onOpenChange }: AffiliateSignupModalProps) => {
  // Form state
  const [profiles, setProfiles] = useState<SocialProfile[]>([{ platform: '', handle: '', otherPlatform: '' }]);
  const [website, setWebsite] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'paypal' | 'jamaican_bank'>('paypal');
  const [paypalEmail, setPaypalEmail] = useState('');
  const [bankInfo, setBankInfo] = useState<JamaicanBankInfo>({
    bankName: '',
    branch: '',
    accountType: 'savings',
    accountNumber: '',
    accountHolderName: ''
  });
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  
  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const { user } = useAuthContext();
  const { userProfile } = useUserProfile();
  const { submitApplication } = useAffiliate();
  const { toast } = useToast();

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setProfiles([{ platform: '', handle: '', otherPlatform: '' }]);
      setWebsite('');
      setPaymentMethod('paypal');
      setPaypalEmail('');
      setBankInfo({
        bankName: '',
        branch: '',
        accountType: 'savings',
        accountNumber: '',
        accountHolderName: ''
      });
      setAgreeToTerms(false);
      setErrors({});
    }
  }, [isOpen]);

  // Add new social profile
  const addProfile = () => {
    setProfiles([...profiles, { platform: '', handle: '', otherPlatform: '' }]);
  };

  // Remove social profile
  const removeProfile = (index: number) => {
    if (profiles.length > 1) {
      setProfiles(profiles.filter((_, i) => i !== index));
    }
  };

  // Update social profile
  const updateProfile = (index: number, field: 'platform' | 'handle' | 'otherPlatform', value: string) => {
    const updatedProfiles = profiles.map((profile, i) => 
      i === index ? { ...profile, [field]: value } : profile
    );
    setProfiles(updatedProfiles);
  };

  // Validation
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate social profiles
    const validProfiles = profiles.filter(p => {
      if (!p.platform || !p.handle) return false;
      if (p.platform === 'Other' && !p.otherPlatform) return false;
      return true;
    });
    if (validProfiles.length === 0) {
      newErrors.profiles = 'At least one social media profile is required';
    }

    // Validate payment info
    if (paymentMethod === 'paypal') {
      if (!paypalEmail.trim()) {
        newErrors.paypalEmail = 'PayPal email is required';
      } else if (!/\S+@\S+\.\S+/.test(paypalEmail)) {
        newErrors.paypalEmail = 'Please enter a valid email address';
      }
    } else {
      if (!bankInfo.bankName) newErrors.bankName = 'Bank name is required';
      if (!bankInfo.branch.trim()) newErrors.branch = 'Branch is required';
      if (!bankInfo.accountNumber.trim()) newErrors.accountNumber = 'Account number is required';
      if (!bankInfo.accountHolderName.trim()) newErrors.accountHolderName = 'Account holder name is required';
    }

    // Validate terms agreement
    if (!agreeToTerms) {
      newErrors.terms = 'You must agree to the affiliate terms';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit application
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setIsLoading(true);
      
      // Filter out empty profiles
      const validProfiles = profiles.filter(p => {
        if (!p.platform || !p.handle) return false;
        if (p.platform === 'Other' && !p.otherPlatform) return false;
        return true;
      });
      
      // Prepare payment info
      const paymentInfo: PaymentInfo = {
        type: paymentMethod,
        ...(paymentMethod === 'paypal' ? { paypalEmail } : { bankInfo })
      };

      await submitApplication(validProfiles, website || undefined, paymentInfo);
      
      toast({
        title: 'Application Submitted!',
        description: 'Your affiliate application has been submitted successfully. We\'ll review it and get back to you soon.',
      });
      
      onOpenChange(false);
    } catch (error) {
      console.error('Error submitting application:', error);
      toast({
        title: 'Submission Failed',
        description: 'There was an error submitting your application. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl w-full max-h-[90vh] p-0 gap-0 bg-white rounded-lg shadow-xl border-0 flex flex-col">
        <DialogClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground z-10">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogClose>

        <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4 bg-gradient-to-r from-jamaica-green to-jamaica-green/90">
          <DialogTitle className="text-2xl font-bold text-white text-center">
            Apply to Become an Affiliate
          </DialogTitle>
          <DialogDescription className="text-white/90 text-center">
            {user ? (
              <span>Welcome, <strong>{userProfile?.name || user.email?.split('@')[0] || 'there'}</strong>! Fill out the form below to apply for our affiliate program.</span>
            ) : (
              <span>Join our affiliate program and earn commissions by promoting JamPack'd products.</span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="p-6 pb-4">

            <div className="space-y-6">
              {/* Social Media Profiles */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-jamaica-green" />
                  <Label className="text-base font-semibold text-gray-900">Social Media Profiles</Label>
                </div>
                <div className="space-y-3">
                  {profiles.map((profile, index) => (
                    <div key={index} className="space-y-2">
                      <div className="flex gap-2 items-start">
                        <div className="flex-1">
                          <Select 
                            value={profile.platform} 
                            onValueChange={(value) => updateProfile(index, 'platform', value)}
                            disabled={false}
                          >
                            <SelectTrigger className="py-2">
                              <SelectValue placeholder="Select platform" />
                            </SelectTrigger>
                            <SelectContent className="z-[10002]" position="popper" sideOffset={4}>
                              {socialPlatforms.map(platform => (
                                <SelectItem key={platform} value={platform}>
                                  {platform}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex-1">
                          <Input
                            placeholder={profile.platform === 'Other' ? 'URL' : 'Handle/Username'}
                            value={profile.handle}
                            onChange={(e) => updateProfile(index, 'handle', e.target.value)}
                            className="py-2"
                          />
                        </div>
                        <div className="flex gap-1">
                          {profiles.length > 1 && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => removeProfile(index)}
                              className="bg-white border-gray-300 hover:bg-gray-50"
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                          )}
                          {index === profiles.length - 1 && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={addProfile}
                              className="bg-white border-gray-300 hover:bg-gray-50"
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                      {profile.platform === 'Other' && (
                        <div className="pl-0">
                          <Input
                            placeholder="Please specify the platform"
                            value={profile.otherPlatform || ''}
                            onChange={(e) => updateProfile(index, 'otherPlatform', e.target.value)}
                            className="py-2"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                  {errors.profiles && (
                    <div className="flex items-center text-red-600 text-sm bg-red-50 p-3 rounded-md">
                      <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                      {errors.profiles}
                    </div>
                  )}
                </div>
              </div>

              {/* Website/Blog */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Globe className="h-5 w-5 text-jamaica-green" />
                  <Label className="text-base font-semibold text-gray-900">Website/Blog</Label>
                  <Badge variant="secondary">Optional</Badge>
                </div>
                <div className="relative">
                  <Globe className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <Input
                    placeholder="https://yourwebsite.com"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    className="pl-10 py-2"
                  />
                </div>
              </div>

              {/* Payment Information */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-jamaica-green" />
                  <Label className="text-base font-semibold text-gray-900">Payment Information</Label>
                </div>
                <div className="space-y-2">
                  <Label className="text-base font-medium text-gray-700">Payment Method</Label>
                  <Select value={paymentMethod} onValueChange={(value: 'paypal' | 'jamaican_bank') => setPaymentMethod(value)} disabled={false}>
                    <SelectTrigger className="py-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-[10002]" position="popper" sideOffset={4}>
                      <SelectItem value="paypal">PayPal</SelectItem>
                      <SelectItem value="jamaican_bank">Jamaican Bank Account</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {paymentMethod === 'paypal' ? (
                  <div className="space-y-2">
                    <Label htmlFor="paypalEmail" className="text-base font-medium text-gray-700">PayPal Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <Input
                        id="paypalEmail"
                        type="email"
                        placeholder="your-paypal@email.com"
                        value={paypalEmail}
                        onChange={(e) => setPaypalEmail(e.target.value)}
                        className="pl-10 py-2"
                      />
                    </div>
                    {errors.paypalEmail && (
                      <div className="flex items-center text-red-600 text-sm bg-red-50 p-3 rounded-md">
                        <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                        {errors.paypalEmail}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-base font-medium text-gray-700">Bank Name</Label>
                        <Select 
                          value={bankInfo.bankName} 
                          onValueChange={(value) => setBankInfo({...bankInfo, bankName: value})}
                          disabled={false}
                        >
                          <SelectTrigger className="py-2">
                            <SelectValue placeholder="Select bank" />
                          </SelectTrigger>
                          <SelectContent className="z-[10002]" position="popper" sideOffset={4}>
                            {jamaicanBanks.map(bank => (
                              <SelectItem key={bank} value={bank}>
                                {bank}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.bankName && (
                          <div className="flex items-center text-red-600 text-sm bg-red-50 p-3 rounded-md">
                            <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                            {errors.bankName}
                          </div>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="branch" className="text-base font-medium text-gray-700">Branch</Label>
                        <div className="relative">
                          <Building className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                          <Input
                            id="branch"
                            placeholder="Branch name/location"
                            value={bankInfo.branch}
                            onChange={(e) => setBankInfo({...bankInfo, branch: e.target.value})}
                            className="pl-10 py-2"
                          />
                        </div>
                        {errors.branch && (
                          <div className="flex items-center text-red-600 text-sm bg-red-50 p-3 rounded-md">
                            <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                            {errors.branch}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-base font-medium text-gray-700">Account Type</Label>
                        <Select 
                          value={bankInfo.accountType} 
                          onValueChange={(value: 'savings' | 'chequing') => setBankInfo({...bankInfo, accountType: value})}
                          disabled={false}
                        >
                          <SelectTrigger className="py-2">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="z-[10002]" position="popper" sideOffset={4}>
                            {accountTypes.map(type => (
                              <SelectItem key={type.value} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="accountNumber" className="text-base font-medium text-gray-700">Account Number</Label>
                        <div className="relative">
                          <CreditCard className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                          <Input
                            id="accountNumber"
                            placeholder="Your account number"
                            value={bankInfo.accountNumber}
                            onChange={(e) => setBankInfo({...bankInfo, accountNumber: e.target.value})}
                            className="pl-10 py-2"
                          />
                        </div>
                        {errors.accountNumber && (
                          <div className="flex items-center text-red-600 text-sm bg-red-50 p-3 rounded-md">
                            <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                            {errors.accountNumber}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="accountHolderName" className="text-base font-medium text-gray-700">Account Holder Full Name</Label>
                      <div className="relative">
                        <UserPlus className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                        <Input
                          id="accountHolderName"
                          placeholder="Full name as it appears on your account"
                          value={bankInfo.accountHolderName}
                          onChange={(e) => setBankInfo({...bankInfo, accountHolderName: e.target.value})}
                          className="pl-10 py-2"
                        />
                      </div>
                      {errors.accountHolderName && (
                        <div className="flex items-center text-red-600 text-sm bg-red-50 p-3 rounded-md">
                          <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                          {errors.accountHolderName}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Terms Agreement */}
              <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <Checkbox 
                  id="terms" 
                  checked={agreeToTerms}
                  onCheckedChange={(checked) => setAgreeToTerms(checked as boolean)}
                  className="mt-0.5"
                />
                <div className="space-y-1">
                  <Label 
                    htmlFor="terms"
                    className="text-sm font-medium text-gray-900 cursor-pointer leading-none"
                  >
                    I agree to the Affiliate Terms and Conditions
                  </Label>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    By checking this box, you agree to our affiliate program terms and commission structure.
                  </p>
                  {errors.terms && (
                    <div className="flex items-center text-red-600 text-sm bg-red-50 p-3 rounded-md">
                      <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                      {errors.terms}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex-shrink-0 p-6 bg-gray-50 border-t border-gray-200">
          <div className="flex justify-end gap-3">
            <Button 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              className="px-6 py-6 font-medium"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={isLoading}
              className="bg-jamaica-green hover:bg-jamaica-green/90 text-white px-8 py-6 font-semibold"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Application'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AffiliateSignupModal;